import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import {
    Box,
    Button,
    Divider,
    Switch,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Typography,
} from '@mui/material';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import InvoicingProductService from 'shared/db/services/InvoicingProduct';
import { ProductInvoiceModel } from 'shared/types/Products';

import { BackLinkButton } from '@/components';
import {
    AddEditInvoicingProductModal,
    FormValues,
    Product,
} from '@/components/AddEditInvoicingProductModal';
import { useImagesContext } from '@/context/ImagesContext';
import { useOrgUserContext } from '@/context/OrgUserContext';
import { useDatabase } from '@/hooks';
import { useRegion } from '@/hooks/useRegion';
import { ROUTE } from '@/router/routes';
import { databaseValidation } from '@/services/databaseValidation';
import { dbServiceLogger } from '@/services/logger/logger';
import { COLOR } from '@/theme/colors';

function InvoicingProductsPage() {
    const { t } = useTranslation();
    const { getDatabase } = useDatabase();
    const { orgUser } = useOrgUserContext();
    const { ImagesService } = useImagesContext();
    const userRegion = useRegion();
    const currencySymbol = userRegion?.currencySymbol || '$';

    const [products, setProducts] = useState<Product[]>([]);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingProduct, setEditingProduct] = useState<Product | null>(null);

    const database = useMemo(() => getDatabase(), [getDatabase]);

    const invoicingProductService = useMemo(
        () =>
            new InvoicingProductService({
                database: getDatabase(),
                logger: dbServiceLogger,
                imageService: ImagesService,
                validation: databaseValidation,
            }),
        [getDatabase, ImagesService],
    );

    useEffect(() => {
        if (!database) return;

        const collection =
            database.collections.get<ProductInvoiceModel>('invoicing_products');
        const subscription = collection
            .query()
            .observeWithColumns(['name', 'price', 'available'])
            .subscribe((records) => {
                setProducts(
                    records.map((r) => ({
                        id: r.id,
                        name: r.name,
                        price: r.price,
                        available: r.available,
                    })),
                );
            });

        return () => subscription.unsubscribe();
    }, [database]);

    const handleOpenAddModal = useCallback(() => {
        setEditingProduct(null);
        setIsModalOpen(true);
    }, []);

    const handleOpenEditModal = useCallback((product: Product) => {
        setEditingProduct(product);
        setIsModalOpen(true);
    }, []);

    const handleCloseModal = useCallback(() => {
        setIsModalOpen(false);
        setEditingProduct(null);
    }, []);

    const handleSave = useCallback(
        async (values: FormValues) => {
            if (editingProduct) {
                await invoicingProductService.updateProduct({
                    id: editingProduct.id,
                    name: values.name.trim(),
                    price: values.price,
                    available: values.available,
                    organisationID: orgUser?.organisationId || '',
                    userID: orgUser?.userId || '',
                });
            } else {
                await invoicingProductService.addProduct({
                    name: values.name.trim(),
                    price: values.price,
                    available: values.available,
                    organisationID: orgUser?.organisationId || '',
                    userID: orgUser?.userId || '',
                });
            }

            handleCloseModal();
        },
        [invoicingProductService, editingProduct, orgUser, handleCloseModal],
    );

    const handleToggleAvailable = useCallback(
        async (product: Product) => {
            await invoicingProductService.updateProduct({
                id: product.id,
                name: product.name,
                price: product.price,
                available: !product.available,
                organisationID: orgUser?.organisationId || '',
                userID: orgUser?.userId || '',
            });
        },
        [invoicingProductService, orgUser],
    );

    const isEmpty = products.length === 0;

    return (
        <Box sx={{ pb: 4 }}>
            <Box
                sx={{
                    alignItems: 'center',
                    display: 'flex',
                    justifyContent: 'space-between',
                    mb: 2,
                }}
            >
                <BackLinkButton
                    to={ROUTE.settings}
                    text="Settings"
                    testID="InvoicingProductsPage-BackButton"
                />

                {!isEmpty && (
                    <Button
                        variant="outlined"
                        startIcon={<AddCircleOutlineIcon />}
                        onClick={handleOpenAddModal}
                        sx={{
                            fontWeight: 700,
                            color: COLOR.black,
                            borderColor: COLOR.lightGray,
                            '&:hover': {
                                borderColor: COLOR.lightGray,
                            },
                        }}
                        data-test-id="InvoicingProductsPage-AddProductButton"
                    >
                        {t('InvoiceSystem:manage_products:add_product')}
                    </Button>
                )}
            </Box>

            {isEmpty ? (
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        minHeight: 400,
                        textAlign: 'center',
                        px: 4,
                    }}
                >
                    <Box
                        sx={{
                            width: 90,
                            height: 90,
                            mb: 2,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            borderRadius: 2,
                            backgroundColor: 'rgba(107, 142, 35, 0.15)',
                        }}
                    >
                        <Typography
                            sx={{
                                fontSize: 48,
                                color: 'rgba(107, 142, 35, 0.4)',
                            }}
                        >
                            $
                        </Typography>
                    </Box>
                    <Typography variant="h6" fontWeight="bold" sx={{ mb: 1 }}>
                        {t('InvoiceSystem:manage_products:products')}
                    </Typography>
                    <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mb: 1, maxWidth: 350 }}
                    >
                        {t(
                            'InvoiceSystem:manage_products:sell_products_directly',
                        )}
                    </Typography>
                    <Typography
                        variant="body2"
                        fontWeight="bold"
                        sx={{ mb: 3, maxWidth: 350 }}
                    >
                        {t(
                            'InvoiceSystem:manage_products:add_your_own_products',
                        )}
                    </Typography>
                    <Button
                        variant="outlined"
                        startIcon={<AddCircleOutlineIcon />}
                        onClick={handleOpenAddModal}
                        sx={{
                            fontWeight: 700,
                            color: COLOR.black,
                            borderColor: COLOR.lightGray,
                            '&:hover': {
                                borderColor: COLOR.lightGray,
                            },
                        }}
                        data-test-id="InvoicingProductsPage-AddProductEmptyButton"
                    >
                        {t('InvoiceSystem:manage_products:add_product')}
                    </Button>
                </Box>
            ) : (
                <>
                    <Divider sx={{ mb: 2 }} />
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell
                                    sx={{ fontWeight: 600, width: '100%' }}
                                >
                                    {t('Procedure:label:name')}
                                </TableCell>
                                <TableCell
                                    sx={{
                                        fontWeight: 600,
                                        whiteSpace: 'nowrap',
                                        minWidth: 120,
                                    }}
                                >
                                    {t('InvoiceSystem:add_edit_products:price')}
                                </TableCell>
                                <TableCell
                                    sx={{
                                        fontWeight: 600,
                                        whiteSpace: 'nowrap',
                                    }}
                                >
                                    {t(
                                        'InvoiceSystem:add_edit_products:available',
                                    )}
                                </TableCell>
                                <TableCell />
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {products.map((product) => (
                                <TableRow
                                    key={product.id}
                                    sx={{
                                        backgroundColor: COLOR.lightBlue,
                                        '&:hover': {
                                            backgroundColor: '#c8e2f5',
                                        },
                                    }}
                                >
                                    <TableCell sx={{ width: '100%' }}>
                                        {product.name}
                                    </TableCell>
                                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                        {currencySymbol}
                                        {Number(product.price).toFixed(2)}
                                    </TableCell>
                                    <TableCell>
                                        <Switch
                                            checked={product.available}
                                            onChange={() =>
                                                handleToggleAvailable(product)
                                            }
                                            data-test-id={`InvoicingProductsPage-AvailableSwitch-${product.id}`}
                                        />
                                    </TableCell>
                                    <TableCell align="right">
                                        <Button
                                            onClick={() =>
                                                handleOpenEditModal(product)
                                            }
                                            sx={{
                                                textTransform: 'none',
                                                fontWeight: 600,
                                            }}
                                            data-test-id={`InvoicingProductsPage-EditButton-${product.id}`}
                                        >
                                            {t('Actions:edit')}
                                        </Button>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </>
            )}

            <AddEditInvoicingProductModal
                isOpen={isModalOpen}
                onClose={handleCloseModal}
                onSave={handleSave}
                product={editingProduct}
            />
        </Box>
    );
}

export default InvoicingProductsPage;
